import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { OxResponse } from './response/OxResponse';
import {
  OrganizationMember,
  UserOrganization,
} from '@app/typedef/define/user/UserOrganization';
import {
  OrganizationMemberCodec,
  UserOrganizationCodec,
} from '@app/typedef/codec/user/UserOrganizationCodec';

/**
 * 用户组织服务（目标 account 后端，基址 {@code environment.account}）。
 *
 * 组织数据存在账号服务里，点表归属仍然只是账号个人 —— 本服务只负责组织本体的增删改查
 * 与成员管理，不掺点表。身份由 JwtInterceptor 追加的 Bearer 令牌携带，方法不传主体 id。
 */
@Injectable({ providedIn: 'root' })
export class UserOrganizationService {
  private account: string = environment.account;

  constructor(private http: HttpClient) {}

  /** 当前账号所属的全部组织（GET /user/organization/many，需登录） */
  getOrganizations(): Observable<UserOrganization[]> {
    return this.http
      .get<OxResponse>(`${this.account}/user/organization/many`)
      .pipe(map((r) => UserOrganizationCodec.decodeArray(r.data)));
  }

  /** 单条组织详情（GET /one/{organizationId}，需登录且是该组织成员） */
  getOrganization(organizationId: string): Observable<UserOrganization> {
    return this.http
      .get<OxResponse>(`${this.account}/user/organization/one/${organizationId}`)
      .pipe(map((r) => UserOrganizationCodec.decode(r.data)));
  }

  /** 创建组织（POST /one/{organizationId}）。组织代码由调用方给定，body 只带名称。 */
  createOrganization(organizationId: string, name: string): Observable<void> {
    return this.http
      .post<OxResponse>(`${this.account}/user/organization/one/${organizationId}`, { name })
      .pipe(map(() => undefined));
  }

  /** 更新组织名称（PUT /one/{organizationId}）。 */
  updateOrganizationName(organizationId: string, name: string): Observable<void> {
    return this.http
      .put<OxResponse>(`${this.account}/user/organization/one/${organizationId}`, { name })
      .pipe(map(() => undefined));
  }

  /** 删除组织（DELETE /one/{organizationId}）。 */
  removeOrganization(organizationId: string): Observable<void> {
    return this.http
      .delete<OxResponse>(`${this.account}/user/organization/one/${organizationId}`)
      .pipe(map(() => undefined));
  }

  /** 添加成员（POST /member/{organizationId}）。 */
  addOrganizationMember(organizationId: string, member: OrganizationMember): Observable<void> {
    return this.http
      .post<OxResponse>(`${this.account}/user/organization/member/${organizationId}`, OrganizationMemberCodec.encode(member))
      .pipe(map(() => undefined));
  }

  /** 修改成员（角色等）（PUT /member/{organizationId}）。 */
  updateOrganizationMember(organizationId: string, member: OrganizationMember): Observable<void> {
    return this.http
      .put<OxResponse>(`${this.account}/user/organization/member/${organizationId}`, OrganizationMemberCodec.encode(member))
      .pipe(map(() => undefined));
  }

  /** 移除成员（DELETE /member/{organizationId}?memberId=...）。 */
  removeOrganizationMember(organizationId: string, memberId: string): Observable<void> {
    return this.http
      .delete<OxResponse>(`${this.account}/user/organization/member/${organizationId}`, {
        params: { memberId },
      })
      .pipe(map(() => undefined));
  }
}